import { useMutation } from "@tanstack/react-query";
import { apiRequest } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";
import { uploadFileToAzure } from "@/hooks/usePhotos";
import type {
  FeedMediaUploadUrlRequest,
  FeedMediaUploadUrlResponse,
} from "@/types/generated/cms.generated";

interface FeedMediaUploadOptions {
  /** The noun in the failure toast's fallback ("Failed to upload <label>…"); default "image". */
  errorLabel?: string;
}

/** A failure with no server answer at all (fetch's TypeError, e.g. "Failed to fetch"). */
export const isNetworkError = (error: unknown): boolean => error instanceof TypeError;

/**
 * The upload failure text: a server/upload `error.message` first; the friendly
 * "Failed to upload <label>. Please try again." for a network-level failure or no message.
 */
export function uploadErrorMessage(error: unknown, label: string): string {
  const fallback = `Failed to upload ${label}. Please try again.`;
  if (isNetworkError(error)) return fallback;
  const message = (error as { message?: unknown } | null)?.message;
  return typeof message === "string" && message ? message : fallback;
}

interface UploadVariables {
  file: File;
  contentType?: string;
}

/**
 * Shared SAS upload workflow for feed media (stickers, kudos badges) and app splashes.
 *
 * Mirrors the photo-upload pattern used elsewhere in cms-portal:
 *   1. POST `{uploadUrlEndpoint}` to request a short-lived SAS upload URL.
 *   2. PUT the file bytes directly to Azure Blob storage (x-ms-blob-type: BlockBlob).
 *   3. The returned `blobUrl` is then stored on the sticker / kudos type / splash entity.
 *
 * `uploadImage(file, contentType?)`: an explicit content type (e.g. `application/json` for a
 * Lottie whose `file.type` is empty) is used for both the upload-url request and the PUT header.
 *
 * @param uploadUrlEndpoint e.g. "/feed-stickers/upload-url", "/kudos-types/upload-url", "/app-splashes/upload-url"
 */
export function useFeedMediaUpload(uploadUrlEndpoint: string, options: FeedMediaUploadOptions = {}) {
  const { toast } = useToast();
  const errorLabel = options.errorLabel ?? "image";

  const mutation = useMutation({
    mutationFn: async ({ file, contentType }: UploadVariables): Promise<string> => {
      const request: FeedMediaUploadUrlRequest = {
        contentType: contentType || file.type || "application/octet-stream",
      };

      const uploadInfo = await apiRequest<FeedMediaUploadUrlResponse>(
        "POST",
        uploadUrlEndpoint,
        request,
      );

      if (!uploadInfo.uploadUrl || !uploadInfo.blobUrl) {
        throw new Error("No upload URL received");
      }

      await uploadFileToAzure(file, uploadInfo.uploadUrl, contentType);

      return uploadInfo.blobUrl;
    },
    onError: (error: unknown) => {
      toast({
        title: "Upload failed",
        description: uploadErrorMessage(error, errorLabel),
        variant: "destructive",
      });
    },
  });

  return {
    /** Uploads a file and resolves with the persisted blob URL. */
    uploadImage: (file: File, contentType?: string) => mutation.mutateAsync({ file, contentType }),
    isUploading: mutation.isPending,
  };
}
